import React, {useState, useContext, useRef} from 'react'
import {Box, Button, Grid, TextField, MenuItem, CircularProgress} from '@material-ui/core/'
import {Mutation} from 'react-apollo'
import {libraryPageQuery} from '../../graphql/barcode/queries/libraryPageQuery'
import {createEditBarcodeSheet} from '../../graphql/barcode/mutations/barcodeSheetMutation'
import {useTranslation} from 'react-i18next'
import useStyles from './BarcodeLibraryEditSheet.styles'
import {ToastMessageContext} from '../../context/ToastMessage'

export default function BarcodeLibraryEditSheet(props) {
  const classes = useStyles()
  const {t} = useTranslation()
  const accessToken = JSON.parse(window.localStorage.getItem('token'))
  const userId = parseInt(window.localStorage.getItem('user_id'))
  const [formValues, setFormValues] = useState({
    name: props.record.name,
    pageSize: props.record.size,
    unit: props.record.unit,
    pageWidth: props.record.width,
    pageHeight: props.record.height,
    topMargin: props.record.topMargin,
    bottomMargin: props.record.bottomMargin,
    leftMargin: props.record.leftMargin,
    rightMargin: props.record.rightMargin,
    rows: props.record.rows,
    rowGutter: props.record.rowGutter,
    columns: props.record.columns,
    columnGutter: props.record.columnGutter,
  })
  const [nameError, setNameError] = useState(false)
  const [updateTemplate, setUpdateTemplate] = useState(false)
  const [labelWidth, setLabelWidth] = useState(
    (
      (props.record.width -
        props.record.leftMargin -
        props.record.rightMargin -
        (props.record.columns - 1) * props.record.columnGutter) /
      props.record.columns
    ).toFixed(4)
  )
  const [labelHeight, setLabelHeight] = useState(
    (
      (props.record.height -
        props.record.topMargin -
        props.record.bottomMargin -
        (props.record.rows - 1) * props.record.rowGutter) /
      props.record.rows
    ).toFixed(4)
  )
  const [forViewing] = useState(props.record.accountId === null)
  const toast = useContext(ToastMessageContext)
  const refBtnSave = useRef(null)

  const handleChange = textField => event => {
    let value = event.target.value
    if (textField === 'pageSize') {
      if (value === 'Letter') {
        setFormValues({
          ...formValues,
          [textField]: value,
          unit: 'in',
          pageWidth: 8.5,
          pageHeight: 11,
        })
      } else if (value === 'A4') {
        setFormValues({
          ...formValues,
          [textField]: value,
          unit: 'mm',
          pageWidth: 210,
          pageHeight: 297,
        })
      } else {
        setFormValues({
          ...formValues,
          [textField]: value,
        })
      }
    } else {
      if (
        textField !== 'name' &&
        textField !== 'pageSize' &&
        textField !== 'unit' &&
        value === ''
      ) {
        value = 0
      }

      setFormValues({
        ...formValues,
        [textField]: value,
      })
    }
    labelWidthHeightCalculation(textField, value)
  }

  function labelWidthHeightCalculation(field, value) {
    formValues[field] = value

    let w = 0
    let h = 0
    if (value === 'A4') {
      w = 210
      h = 297
    } else if (value === 'Letter') {
      w = 8.5
      h = 11
    } else {
      w = formValues.pageWidth
      h = formValues.pageHeight
    }

    setLabelWidth(
      (
        (w -
          formValues.leftMargin -
          formValues.rightMargin -
          (formValues.columns - 1) * formValues.columnGutter) /
        formValues.columns
      ).toFixed(4)
    )
    setLabelHeight(
      (
        (h -
          formValues.topMargin -
          formValues.bottomMargin -
          (formValues.rows - 1) * formValues.rowGutter) /
        formValues.rows
      ).toFixed(4)
    )
  }

  function handleSave(barcodeSheet, event) {
    if (checkValidity()) {
      barcodeSheet({
        variables: {
          id: props.record.id,
          accountId: parseInt(props.record.accountId),
          name: formValues.name,
          size: formValues.pageSize,
          unit: formValues.unit,
          width: parseFloat(formValues.pageWidth),
          height: parseFloat(formValues.pageHeight),
          topMargin: parseFloat(formValues.topMargin),
          bottomMargin: parseFloat(formValues.bottomMargin),
          leftMargin: parseFloat(formValues.leftMargin),
          rightMargin: parseFloat(formValues.rightMargin),
          rows: parseInt(formValues.rows),
          columns: parseInt(formValues.columns),
          rowGutter: parseFloat(formValues.rowGutter),
          columnGutter: parseFloat(formValues.columnGutter),
        },
      })
      props.disableButton(true)
      setUpdateTemplate(true)
    }
  }

  function mutationLoad(data) {
    props.disableButton(false)
    setUpdateTemplate(false)
    if (data['createEditBarcodeSheet']['errors'].length > 0) {
      setNameError(true)
      toast.popMessage(
        t('barcode.wizard.create-barcode-sheet.error-name-taken'),
        'error',
        refBtnSave.current
      )
    } else {
      toast.popMessage(
        t('barcode.library.edit-barcode-sheet.success-edit'),
        'success',
        refBtnSave.current
      )
    }
  }

  function checkValidity() {
    let errorMessage = ''
    setNameError(false)
    if (formValues.name === '') {
      errorMessage = t('barcode.library.edit-barcode-sheet.name-blank')
      setNameError(true)
    } else if (formValues.rows < 0 || formValues.columns < 0) {
      errorMessage = t('barcode.wizard.create-barcode-sheet.rows-column-negative')
    } else if (parseInt(formValues.rows) === 0 || parseInt(formValues.columns) === 0) {
      errorMessage = t('barcode.wizard.create-barcode-sheet.rows-column-error')
    }

    if (errorMessage === '') {
      return true
    } else {
      toast.popMessage(errorMessage, 'error', refBtnSave.current)
      return false
    }
  }

  return (
    <div>
      <Grid container spacing={2}>
        <Grid container item xs={12}>
          <TextField
            id="name"
            label={t('barcode.library.edit-barcode-sheet.name')}
            value={formValues.name}
            fullWidth
            variant="outlined"
            onChange={handleChange('name')}
            error={nameError}
            disabled={updateTemplate || forViewing}
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-select-sheet-size"
            select
            label={t('barcode.library.edit-barcode-sheet.page-size')}
            className={classes.menuBar}
            value={formValues.pageSize}
            fullWidth={true}
            onChange={handleChange('pageSize')}
            SelectProps={{
              MenuProps: {
                getContentAnchorEl: null,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
              },
            }}
            variant="outlined"
            disabled={updateTemplate || forViewing}
          >
            {['Letter', 'A4', 'Custom'].map(sheetSize => (
              <MenuItem key={sheetSize} value={sheetSize}>
                {sheetSize}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-select-sheet-unit"
            select
            label={t('barcode.library.edit-barcode-sheet.unit')}
            className={classes.menuBar}
            value={formValues.unit}
            fullWidth={true}
            onChange={handleChange('unit')}
            SelectProps={{
              MenuProps: {
                getContentAnchorEl: null,
                anchorOrigin: {
                  vertical: 'bottom',
                  horizontal: 'left',
                },
              },
            }}
            variant="outlined"
            disabled={
              formValues.pageSize === 'Letter' || formValues.pageSize === 'A4' || forViewing
            }
          >
            {['in', 'mm'].map(sheetUnit => (
              <MenuItem key={sheetUnit} value={sheetUnit}>
                {sheetUnit}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-page-width"
            label={t('barcode.library.edit-barcode-sheet.page-width')}
            type="number"
            value={formValues.pageWidth}
            fullWidth={true}
            onChange={handleChange('pageWidth')}
            variant="outlined"
            disabled={
              formValues.pageSize === 'Letter' || formValues.pageSize === 'A4' || forViewing
            }
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-page-height"
            label={t('barcode.library.edit-barcode-sheet.page-height')}
            type="number"
            value={formValues.pageHeight}
            fullWidth={true}
            onChange={handleChange('pageHeight')}
            variant="outlined"
            disabled={
              formValues.pageSize === 'Letter' || formValues.pageSize === 'A4' || forViewing
            }
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-page-top-margin"
            label={t('barcode.library.edit-barcode-sheet.top-margin')}
            type="number"
            value={formValues.topMargin}
            fullWidth={true}
            onChange={handleChange('topMargin')}
            variant="outlined"
            disabled={forViewing}
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-page-bottom-margin"
            label={t('barcode.library.edit-barcode-sheet.bottom-margin')}
            type="number"
            value={formValues.bottomMargin}
            fullWidth={true}
            onChange={handleChange('bottomMargin')}
            variant="outlined"
            disabled={forViewing}
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-page-left-margin"
            label={t('barcode.library.edit-barcode-sheet.left-margin')}
            type="number"
            value={formValues.leftMargin}
            fullWidth={true}
            onChange={handleChange('leftMargin')}
            variant="outlined"
            disabled={forViewing}
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-page-right-margin"
            label={t('barcode.library.edit-barcode-sheet.right-margin')}
            type="number"
            value={formValues.rightMargin}
            fullWidth={true}
            onChange={handleChange('rightMargin')}
            variant="outlined"
            disabled={forViewing}
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-page-row"
            label={t('barcode.library.edit-barcode-sheet.rows')}
            type="number"
            value={formValues.rows}
            fullWidth={true}
            onChange={handleChange('rows')}
            variant="outlined"
            disabled={forViewing}
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-page-row-gutter"
            label={t('barcode.library.edit-barcode-sheet.row-gutter')}
            type="number"
            value={formValues.rowGutter}
            fullWidth={true}
            onChange={handleChange('rowGutter')}
            variant="outlined"
            disabled={forViewing}
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-page-columns"
            label={t('barcode.library.edit-barcode-sheet.columns')}
            type="number"
            value={formValues.columns}
            fullWidth={true}
            onChange={handleChange('columns')}
            variant="outlined"
            disabled={forViewing}
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-page-row-gutter"
            label={t('barcode.library.edit-barcode-sheet.column-gutter')}
            type="number"
            value={formValues.columnGutter}
            fullWidth={true}
            onChange={handleChange('columnGutter')}
            variant="outlined"
            disabled={forViewing}
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-label-width"
            label={t('barcode.library.edit-barcode-sheet.label-width')}
            type="number"
            value={labelWidth}
            fullWidth={true}
            variant="outlined"
            disabled
          />
        </Grid>

        <Grid container item xs={6}>
          <TextField
            id="outlined-label-height"
            label={t('barcode.library.edit-barcode-sheet.label-height')}
            type="number"
            value={labelHeight}
            fullWidth={true}
            variant="outlined"
            disabled
          />
        </Grid>
      </Grid>

      <Box marginTop={2} />
      <Grid container item xs={12} hidden={forViewing}>
        <Mutation
          mutation={createEditBarcodeSheet}
          refetchQueries={() => [
            {
              query: libraryPageQuery,
              variables: {
                accountId: parseInt(props.record.accountId),
                token: accessToken,
                userId: userId,
                filter: '',
              },
            },
          ]}
          awaitRefetchQueries
          onCompleted={data => mutationLoad(data)}
        >
          {(createEditBarcodeSheet, {data}) => (
            <Button
              ref={refBtnSave}
              value="save-template"
              variant="contained"
              color="primary"
              fullWidth={true}
              onClick={e => handleSave(createEditBarcodeSheet, e)}
              disabled={updateTemplate}
              style={{textTransform: 'none'}}
            >
              {updateTemplate ? (
                <div>
                  {t('barcode.library.edit-barcode-sheet.updating')}
                  <CircularProgress size={20} color="secondary" />
                </div>
              ) : (
                t('barcode.library.edit-barcode-sheet.update-barcode')
              )}
            </Button>
          )}
        </Mutation>
      </Grid>
      <Box marginBottom={2} />
    </div>
  )
}
